import { Step, Flows, Flow, DeployStepToRouteMap, DeploySteps, BuildSteps } from "@/types/flows";
import { useCallback, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";

export const useFlowState = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { workspaceUrl, workflowUrl } = useParams<{
        workspaceUrl: string;
        workflowUrl: string;
    }>();
    const { step: stepParam } = useParams<{ step: Step }>();

    const defaultStep = useMemo(() => {
        if (location.pathname.includes("/p/")) {
            return null;
        }
        return BuildSteps.UPLOAD;
    }, [location.pathname]);

    const step = useMemo(() => stepParam || defaultStep, [stepParam, defaultStep]);

    const flow = useMemo(() => {
        if (location.pathname.includes("/p/")) {
            return Flows.SHARE;
        }
        switch (step) {
            case BuildSteps.UPLOAD:
            case BuildSteps.PROMPT:
            case BuildSteps.REVIEW:
            case BuildSteps.PREVIEW: {
                return Flows.BUILD;
            }
            case DeploySteps.DEPLOY:
            case DeploySteps.IMPROVE: {
                return Flows.DEPLOY;
            }
            default:
                // eslint-disable-next-line no-console
                console.error(`Invalid step: ${step}`);
                return Flows.BUILD;
        }
    }, [step, location.pathname]);

    const onlyShowPredictions = useMemo(() => {
        // In IMPROVE step, we show annotations (editable) instead of predictions
        if (step === DeploySteps.IMPROVE) {
            return false;
        }
        return (
            flow === Flows.DEPLOY ||
            step === BuildSteps.REVIEW ||
            step === BuildSteps.PREVIEW ||
            flow === Flows.SHARE
        );
    }, [flow, step]);

    const hasFullVideoInference = useMemo(() => {
        return (
            flow === Flows.DEPLOY ||
            step === BuildSteps.REVIEW ||
            step === BuildSteps.PREVIEW ||
            flow === Flows.SHARE
        );
    }, [flow, step]);

    const computeUrlPath = useCallback(
        (options: {
            flow?: Flow;
            step?: Step | null;
            workspaceUrl?: string | null;
            workflowUrl?: string | null;
            replace?: boolean;
        }) => {
            const workspaceSlug =
                typeof options.workspaceUrl === "string" ? options.workspaceUrl : workspaceUrl;
            const wfSlug =
                typeof options.workflowUrl === "string" ? options.workflowUrl : workflowUrl;
            const flowToUse = options.flow || flow;
            const stepToUse = options.step || step;

            let path: string;

            if (flowToUse === Flows.SHARE) {
                // share flow - preserve the /p/ path structure
                const shareMatch = location.pathname.match(/\/p\/[^\/]+/);
                if (shareMatch && workspaceSlug) {
                    path = `/${workspaceSlug}${shareMatch[0]}`;
                } else {
                    path = `/${workspaceSlug || ""}`;
                }
            } else if (!(flowToUse && stepToUse)) {
                // flow hasn't been started
                path = `/${workspaceSlug || ""}`;
            } else if (flowToUse === Flows.BUILD) {
                // build flow - always use build flow path construction
                path = `/${workspaceSlug ? `${workspaceSlug}/` : ""}${flowToUse}/${stepToUse}`;
            } else if (flowToUse === Flows.DEPLOY && workspaceSlug && wfSlug) {
                // deploy flow - only when we have both workspace and workflow
                path = `/${workspaceSlug}/${wfSlug}/${DeployStepToRouteMap[stepToUse as DeploySteps]}`;
            } else {
                // fallback to build flow path construction
                path = `/${workspaceSlug ? `${workspaceSlug}/` : ""}${flowToUse}/${stepToUse}`;
            }

            return path;
        },
        [workspaceUrl, workflowUrl, flow, step, location.pathname]
    );

    const setStep = useCallback(
        (
            options: {
                flow?: Flow;
                step?: Step | null;
                workspaceUrl?: string | null;
                workflowUrl?: string | null;
                replace?: boolean;
            } = {}
        ) => {
            const path: string = computeUrlPath(options);

            // console.log("navigating to", path);

            navigate(path, { relative: "path", replace: options?.replace || true });
        },
        [navigate, computeUrlPath]
    );

    return { flow, step, setStep, computeUrlPath, onlyShowPredictions, hasFullVideoInference };
};
